import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  BanknoteIcon,
  ChevronDown,
  ClockIcon,
  Eye,
  EyeOff,
  FlaskConical,
  LogIn,
  Mail,
  MailCheck,
  ShieldCheck,
} from 'lucide-react';
import { Badge, Button, FormField, TextInput } from '@micarahr/web-ui';
import { roleLabel } from '@micarahr/permissions/web';
import { env } from '../../config/env';
import { authService } from '../../services';
import type { SignInOptions } from '../../services/auth';
import { signInErrorText } from '../../services/auth.api';
import { DEMO_PASSWORD } from '../../services/demo-credentials';
import { lastSignedInUserId, useAuthStore } from '../../stores';
import { canOpenPath } from '../../app/navigation';
import { router } from '../../app/router';
import { takeSignOutNotice } from '../../components/shell/idle-sign-out';

/**
 * Sign in.
 *
 * A split page: the product on the left, the form on the right. The form column
 * is a fixed, comfortable measure rather than a card stretched to whatever
 * height the panel beside it happens to be — the previous layout put the form
 * in a grid cell that stretched to match the credentials list, leaving a third
 * of the card empty below the button.
 *
 * The demonstration accounts stay on the page and stay labelled as development
 * credentials, but they are now a disclosure below the form rather than a panel
 * competing with it. Hiding them entirely would mean the reader cannot try the
 * roles; giving them equal weight to the sign-in form misrepresents which one
 * is the point of the page.
 */

/*
 * The password is checked in the handler rather than here: with passwords off
 * there is no password field, and the same form only sends a link.
 */
const schema = z.object({
  email: z
    .string()
    .min(1, 'Enter your email address.')
    .email('That does not look like an email address.'),
  password: z.string(),
});

type SignInValues = z.infer<typeof schema>;

/** Claims the system actually keeps — each is enforced server-side, not copy. */
const PROMISES = [
  {
    icon: ClockIcon,
    title: 'Attendance that holds up',
    body: 'Every entry keeps the evidence the device reported. A correction is recorded beside the original, never over it.',
  },
  {
    icon: BanknoteIcon,
    title: 'Payroll you can re-run',
    body: 'Locked periods stay locked, reopening leaves a reason, and every line carries the basis it was calculated from.',
  },
  {
    icon: ShieldCheck,
    title: 'Permission, not presentation',
    body: 'What somebody can see follows their role and scope, and is decided by the server rather than by the screen.',
  },
];

function Wordmark({ tone = 'ink' }: { tone?: 'ink' | 'inverse' }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span
        className={
          tone === 'inverse'
            ? 'text-2xl font-extrabold tracking-tight text-ink-inverse'
            : 'text-2xl font-extrabold tracking-tight text-ink'
        }
      >
        Micara
      </span>
      <span
        className={
          tone === 'inverse'
            ? 'text-2xl font-extrabold tracking-tight text-white/70'
            : 'text-2xl font-extrabold tracking-tight text-brand'
        }
      >
        HR
      </span>
    </div>
  );
}

/** The address an emailed link lands on. Not a router route: nobody is signed in yet. */
export const MAGIC_LINK_PATH = '/auth/magic';

/**
 * Sign in, or finish signing in from an emailed link.
 *
 * Like the form, the link's page renders wherever the session is missing —
 * which, for a link, is always /auth/magic.
 */
export function SignInPage() {
  if (window.location.pathname === MAGIC_LINK_PATH) return <MagicLinkPage />;
  return <SignInForm />;
}

/**
 * Adopt a fresh session, landing where the tab was only for the same person.
 *
 * Sign-in renders wherever the tab was. Stay there only for the same person,
 * on a page they may open; anybody else starts at their own home rather than
 * on the last user's page — which, as often as not, they are not allowed to
 * see. An emailed link's own address always goes home.
 */
function useAdoptSession() {
  const setSession = useAuthStore((state) => state.setSession);
  return (
    session: Awaited<ReturnType<typeof authService.signIn>>,
    previousUserId: string | null,
  ) => {
    const path = window.location.pathname;
    const someoneElse = previousUserId !== null && previousUserId !== session.userId;
    if (
      path === MAGIC_LINK_PATH ||
      (path !== '/' && (someoneElse || !canOpenPath(path, session.permissions)))
    ) {
      router.history.replace('/');
    }
    setSession(session);
  };
}

/** The `?error=` a Google sign-in comes back with, read once and taken off the address. */
function takeReturnedError(): string | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('error');
  if (!code) return null;
  params.delete('error');
  const rest = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
  return signInErrorText(code);
}

function SignInForm() {
  const adopt = useAdoptSession();
  const [formError, setFormError] = useState<string | null>(() => takeReturnedError());
  const [showPassword, setShowPassword] = useState(false);
  const [showAccounts, setShowAccounts] = useState(false);
  // Why the last session ended, when it was ended for inactivity. Read once.
  const [notice] = useState(() => takeSignOutNotice());
  const [options, setOptions] = useState<SignInOptions | null>(null);
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState<{ email: string; message: string } | null>(null);

  /*
   * What to offer comes from the server, before the form is drawn: a
   * production console must never show a password field the server refuses.
   * If the server cannot be asked, the emailed link is the safe offer — it
   * works wherever sign-in works at all.
   */
  useEffect(() => {
    let cancelled = false;
    authService
      .signInOptions()
      .then((answer) => {
        if (!cancelled) setOptions(answer);
      })
      .catch(() => {
        if (!cancelled) setOptions({ passwordLogin: false, magicLink: true, google: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const form = useForm<SignInValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const passwordLogin = options?.passwordLogin ?? false;

  const signInWithPassword = form.handleSubmit(async (values) => {
    setFormError(null);
    if (!values.password) {
      form.setError('password', { message: 'Enter your password.' });
      return;
    }
    try {
      const previousUserId = lastSignedInUserId();
      adopt(await authService.signIn(values.email, values.password), previousUserId);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Sign in failed.');
    }
  });

  const sendLink = async () => {
    setFormError(null);
    form.clearErrors('password');
    if (!(await form.trigger('email'))) return;
    const email = form.getValues('email').trim();
    setSendingLink(true);
    try {
      const message = await authService.requestSignInLink(email);
      setLinkSent({ email, message });
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'The link could not be sent.');
    } finally {
      setSendingLink(false);
    }
  };

  const submit = passwordLogin
    ? signInWithPassword
    : form.handleSubmit(async () => {
        await sendLink();
      });

  // Statically empty in production so the account list is stripped, not hidden.
  const accounts = import.meta.env.PROD ? [] : authService.demoAccounts();

  return (
    <SignInLayout>
      <h1 className="mt-8 text-2xl font-semibold text-ink tl:mt-0">Sign in</h1>
      <p className="mt-1.5 text-base text-ink-secondary">
        HR &amp; Administration. Use the work email address your administrator registered.
      </p>

      {notice ? (
        <p
          role="status"
          className="mt-6 rounded-lg border border-info-border bg-info-soft px-3 py-2 text-base text-ink-secondary"
        >
          {notice}
        </p>
      ) : null}

      {linkSent ? (
        <CheckYourEmail
          email={linkSent.email}
          message={linkSent.message}
          resending={sendingLink}
          onResend={() => void sendLink()}
          onChangeEmail={() => {
            setLinkSent(null);
            setFormError(null);
          }}
        />
      ) : !options ? (
        <div className="mt-8 space-y-3" aria-busy>
          <div className="h-10 animate-pulse rounded-md bg-neutral-soft" />
          <div className="h-11 animate-pulse rounded-md bg-neutral-soft" />
        </div>
      ) : (
        <>
          <form onSubmit={submit} className="mt-8 space-y-4" noValidate>
            <FormField label="Email address" required error={form.formState.errors.email?.message}>
              <TextInput
                type="email"
                autoComplete="username"
                placeholder="you@yourcompany.com"
                className="h-10"
                {...form.register('email')}
              />
            </FormField>

            {passwordLogin ? (
              <FormField label="Password" required error={form.formState.errors.password?.message}>
                <div className="relative">
                  <TextInput
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Your password"
                    className="h-10 pr-10"
                    {...form.register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((visible) => !visible)}
                    // Labelled rather than icon-only: a screen reader announcing
                    // "button" beside a password field says nothing useful.
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-ink-subtle transition-colors hover:text-ink-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" aria-hidden />
                    ) : (
                      <Eye className="h-4 w-4" aria-hidden />
                    )}
                  </button>
                </div>
              </FormField>
            ) : null}

            {formError ? (
              <div
                role="alert"
                className="rounded-md border border-danger-border bg-danger-soft px-3 py-2.5"
              >
                <p className="text-base text-danger">{formError}</p>
              </div>
            ) : null}

            {passwordLogin ? (
              <Button
                type="submit"
                variant="primary"
                size="lg"
                block
                loading={form.formState.isSubmitting}
                leadingIcon={<LogIn className="h-4 w-4" />}
              >
                Sign in
              </Button>
            ) : null}

            {options.magicLink ? (
              <Button
                type={passwordLogin ? 'button' : 'submit'}
                variant={passwordLogin ? 'secondary' : 'primary'}
                size="lg"
                block
                loading={sendingLink}
                onClick={passwordLogin ? () => void sendLink() : undefined}
                leadingIcon={<Mail className="h-4 w-4" />}
              >
                Email me a sign-in link
              </Button>
            ) : null}
          </form>

          {options.google ? (
            <>
              <div className="my-5 flex items-center gap-3" aria-hidden>
                <span className="h-px flex-1 bg-line" />
                <span className="text-caption text-ink-muted">or</span>
                <span className="h-px flex-1 bg-line" />
              </div>
              <Button
                type="button"
                variant="secondary"
                size="lg"
                block
                onClick={() => window.location.assign(authService.googleSignInUrl())}
                leadingIcon={<GoogleMark />}
              >
                Continue with Google
              </Button>
            </>
          ) : null}
        </>
      )}

      <p className="mt-5 text-caption leading-relaxed text-ink-muted">
        MicaraHR has no public sign-up. Accounts are created by your HR team. Employees use the
        MicaraHR mobile app for their own records.
      </p>

      {/*
       * Development builds only. A production sign-in page that lists
       * working administrator accounts with their shared password is a
       * published credential, however clearly it is labelled fictional.
       * `import.meta.env.PROD` rather than `env().isProduction` on
       * purpose: the static constant lets the bundler delete this block —
       * and the password literal — from production bundles entirely.
       */}
      {import.meta.env.PROD || !passwordLogin ? null : (
        <div className="mt-8 border-t border-line pt-5">
          <button
            type="button"
            onClick={() => setShowAccounts((open) => !open)}
            aria-expanded={showAccounts}
            aria-controls="development-accounts"
            className="flex w-full items-center gap-2 rounded-md py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
          >
            <FlaskConical className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
            <span className="text-micro font-semibold uppercase tracking-wide text-ink-muted">
              Development credentials
            </span>
            <ChevronDown
              aria-hidden
              className={`ml-auto h-4 w-4 shrink-0 text-ink-subtle transition-transform ${
                showAccounts ? 'rotate-180' : ''
              }`}
            />
          </button>

          {showAccounts ? (
            <div id="development-accounts" className="mt-3">
              <p className="text-caption leading-relaxed text-ink-muted">
                Fictional demonstration accounts. The password for all of them is{' '}
                <code className="rounded bg-neutral-soft px-1 py-0.5 font-mono text-ink">
                  {DEMO_PASSWORD}
                </code>
                . Each opens a different navigation and a different set of permissions.
              </p>

              <ul className="mt-3 space-y-1.5">
                {accounts.map((account) => (
                  <li key={account.email}>
                    <button
                      type="button"
                      onClick={() => {
                        form.setValue('email', account.email, { shouldValidate: true });
                        form.setValue('password', DEMO_PASSWORD, { shouldValidate: true });
                        setShowAccounts(false);
                      }}
                      className="w-full rounded-md border border-line bg-surface px-3 py-2 text-left transition-colors hover:border-brand-border hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
                    >
                      <span className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-base font-medium text-ink">
                          {account.name}
                        </span>
                        {account.roles.map((role) => (
                          <Badge key={role} size="sm" tone="brand">
                            {roleLabel(role as never)}
                          </Badge>
                        ))}
                      </span>
                      <span className="mt-0.5 block truncate text-caption text-ink-muted">
                        {account.email}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </SignInLayout>
  );
}

/**
 * The split page: the product on the left, the form (or the link's progress)
 * on the right.
 */
function SignInLayout({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-surface tl:grid tl:grid-cols-[1fr_minmax(460px,42%)]">
      {/*
       * The brand panel is decorative and carries no control the user needs, so
       * it is dropped below 1024px rather than stacked — on a laptop-width
       * window the form should be the first thing on screen, not the second.
       */}
      <aside className="relative hidden overflow-hidden bg-gradient-to-br from-brand via-brand-hover to-accent tl:flex tl:flex-col tl:justify-between tl:p-12 desk:p-16">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.20),transparent_55%)]"
        />

        <div className="relative">
          <Wordmark tone="inverse" />
          <p className="mt-1 text-md text-white/70">People. Attendance. Payroll. Simplified.</p>
        </div>

        <div className="relative max-w-lg">
          <h2 className="text-3xl font-semibold leading-tight text-ink-inverse">
            The record of what happened, kept properly.
          </h2>

          <ul className="mt-8 space-y-6">
            {PROMISES.map((promise) => (
              <li key={promise.title} className="flex gap-4">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15 ring-1 ring-inset ring-white/20">
                  <promise.icon className="h-4 w-4 text-ink-inverse" aria-hidden />
                </span>
                <div>
                  <p className="text-md font-semibold text-ink-inverse">{promise.title}</p>
                  <p className="mt-1 text-base leading-relaxed text-white/70">{promise.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Only a demonstration build says its data is fictional; production holds real records. */}
        {env().demoMode ? (
          <p className="relative text-caption text-white/60">
            Demonstration environment. All employees, payroll figures and documents shown in this
            system are fictional.
          </p>
        ) : null}
      </aside>

      <main className="flex min-h-screen flex-col justify-center overflow-y-auto px-6 py-12 tp:px-12 tl:px-10 desk:px-16">
        <div className="mx-auto w-full max-w-[400px]">
          <div className="tl:hidden">
            <Wordmark />
            <p className="mt-1 text-base text-ink-muted">
              People. Attendance. Payroll. Simplified.
            </p>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}

/** After a link was asked for: where it went, and what to do next. */
function CheckYourEmail({
  email,
  message,
  resending,
  onResend,
  onChangeEmail,
}: {
  email: string;
  message: string;
  resending: boolean;
  onResend: () => void;
  onChangeEmail: () => void;
}) {
  return (
    <div role="status" className="mt-8 rounded-lg border border-line bg-canvas p-5">
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-soft">
        <MailCheck className="h-5 w-5 text-brand" aria-hidden />
      </span>
      <h2 className="mt-4 text-lg font-semibold text-ink">Check your email</h2>
      <p className="mt-1.5 text-base text-ink-secondary">{message}</p>
      <p className="mt-3 text-base text-ink-secondary">
        Look for a message from MicaraHR at <span className="font-medium text-ink">{email}</span>.
        The link works once and expires in 15 minutes.
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" loading={resending} onClick={onResend}>
          Send another link
        </Button>
        <Button variant="ghost" size="sm" onClick={onChangeEmail}>
          Use a different email
        </Button>
      </div>
    </div>
  );
}

/** Google's "G", drawn here: the icon set carries no brand marks. */
function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" className="h-4 w-4" aria-hidden>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.33-1.58-5.04-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.96 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.28-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3 2.33C4.67 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}

/**
 * Finish signing in from an emailed link.
 *
 * The token is posted once. React's development double-run of effects would
 * otherwise post it twice, and a link is good once: the second post would
 * fail and paint an error over a sign-in that had just worked. Posting from
 * script, not on page load, is also what keeps mail scanners — which fetch
 * every link but run no script — from spending it.
 */
const redemptions = new Map<string, ReturnType<typeof authService.signInWithLink>>();

function MagicLinkPage() {
  const adopt = useAdoptSession();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setError('This link is incomplete. Ask for a new one.');
      return;
    }
    const previousUserId = lastSignedInUserId();
    let redemption = redemptions.get(token);
    if (!redemption) {
      redemption = authService.signInWithLink(token);
      redemptions.set(token, redemption);
    }
    redemption
      .then((session) => adopt(session, previousUserId))
      .catch((failure: unknown) => {
        setError(failure instanceof Error ? failure.message : 'Sign-in did not complete.');
      });
    // Once only, whatever `adopt` is on this render: the link is good once.
  }, []);

  return (
    <SignInLayout>
      {error ? (
        <div className="mt-8 tl:mt-0">
          <h1 className="text-2xl font-semibold text-ink">This link can&apos;t be used</h1>
          <div
            role="alert"
            className="mt-6 rounded-md border border-danger-border bg-danger-soft px-3 py-2.5"
          >
            <p className="text-base text-danger">{error}</p>
          </div>
          <Button
            className="mt-6"
            variant="primary"
            size="lg"
            block
            onClick={() => {
              window.history.replaceState(null, '', '/');
              window.location.reload();
            }}
          >
            Back to sign in
          </Button>
        </div>
      ) : (
        <div role="status" className="mt-8 tl:mt-0">
          <h1 className="text-2xl font-semibold text-ink">Signing you in…</h1>
          <p className="mt-1.5 text-base text-ink-secondary">Checking your sign-in link.</p>
        </div>
      )}
    </SignInLayout>
  );
}
